import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Save, RotateCcw, ArrowLeft, Loader2, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { api } from "@/lib/api";
import { TextField, ImageField, SectionCard } from "@/pages/admin/Fields";

const TABS = [
  { id: "brand", label: "Brand" },
  { id: "hero", label: "Hero" },
  { id: "about", label: "About" },
  { id: "grill", label: "Grill" },
  { id: "menu_flipbook", label: "Menu Pages" },
  { id: "testimonials", label: "Testimonials" },
  { id: "contact", label: "Contact" },
  { id: "footer", label: "Footer" },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { content, refresh, saveContent } = useSiteContent();
  const [draft, setDraft] = useState(null);
  const [tab, setTab] = useState("brand");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (content) setDraft(structuredClone(content));
  }, [content]);

  // Auto-logout when the admin leaves this page via tab close / reload
  useEffect(() => {
    const handleBeforeUnload = () => {
      try {
        sessionStorage.removeItem("aseka_admin_token");
      } catch (e) { /* ignore */ }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const updateField = (section, key, value) => {
    setDraft((d) => ({ ...d, [section]: { ...(d[section] || {}), [key]: value } }));
  };

  const updateArrayItem = (section, arrKey, index, key, value) => {
    setDraft((d) => {
      const arr = [...(d[section]?.[arrKey] || [])];
      arr[index] = { ...arr[index], [key]: value };
      return { ...d, [section]: { ...d[section], [arrKey]: arr } };
    });
  };

  const addArrayItem = (section, arrKey, template) => {
    setDraft((d) => {
      const arr = [...(d[section]?.[arrKey] || []), { ...template }];
      return { ...d, [section]: { ...d[section], [arrKey]: arr } };
    });
  };

  const removeArrayItem = (section, arrKey, index) => {
    setDraft((d) => {
      const arr = [...(d[section]?.[arrKey] || [])];
      arr.splice(index, 1);
      return { ...d, [section]: { ...d[section], [arrKey]: arr } };
    });
  };

  const moveArrayItem = (section, arrKey, index, delta) => {
    setDraft((d) => {
      const arr = [...(d[section]?.[arrKey] || [])];
      const newIdx = index + delta;
      if (newIdx < 0 || newIdx >= arr.length) return d;
      [arr[index], arr[newIdx]] = [arr[newIdx], arr[index]];
      return { ...d, [section]: { ...d[section], [arrKey]: arr } };
    });
  };

  const updateSimpleArrayItem = (section, arrKey, index, value) => {
    setDraft((d) => {
      const arr = [...(d[section]?.[arrKey] || [])];
      arr[index] = value;
      return { ...d, [section]: { ...d[section], [arrKey]: arr } };
    });
  };

  const addSimpleArrayItem = (section, arrKey, value = "") => {
    setDraft((d) => {
      const arr = [...(d[section]?.[arrKey] || []), value];
      return { ...d, [section]: { ...d[section], [arrKey]: arr } };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSaveMsg("");
    try {
      await saveContent(draft);
      setSaveMsg("Changes saved successfully.");
      setTimeout(() => setSaveMsg(""), 3000);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset all content to defaults? This cannot be undone.")) return;
    setSaving(true);
    try {
      const { data } = await api.post("/content/reset");
      setDraft(structuredClone(data.content));
      await refresh();
      setSaveMsg("Content reset to defaults.");
      setTimeout(() => setSaveMsg(""), 3000);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  if (!draft) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center text-cream" data-testid="admin-loading">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg grain text-cream" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[rgba(26,14,8,0.85)] border-b border-warm">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center gap-4">
          <button onClick={() => navigate("/")} data-testid="admin-back-to-site" className="text-cream/70 hover:text-[color:var(--gold-hi)] inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase">
            <ArrowLeft size={14} /> Site
          </button>
          <div className="flex-1">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[color:var(--gold-hi)]">Admin</div>
            <div className="font-serif-display text-xl text-cream">Content Editor</div>
          </div>
          <div className="hidden md:block text-xs text-cream/60 mr-2" data-testid="admin-current-user">
            {user?.email}
          </div>
          <button onClick={handleReset} disabled={saving} data-testid="admin-reset-btn" className="hidden md:inline-flex items-center gap-2 rounded-full border border-warm px-4 py-2 text-xs tracking-wide uppercase hover:border-[color:var(--gold)] hover:text-[color:var(--gold-hi)] transition disabled:opacity-50">
            <RotateCcw size={13} /> Reset
          </button>
          <button onClick={handleSave} disabled={saving} data-testid="admin-save-btn" className="inline-flex items-center gap-2 rounded-full bg-[color:var(--gold)] text-[color:var(--bg)] px-5 py-2 text-xs tracking-wide uppercase font-medium hover:bg-[color:var(--gold-hi)] transition disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? "Saving..." : "Save"}
          </button>
          <button onClick={handleLogout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 rounded-full border border-warm px-4 py-2 text-xs tracking-wide uppercase hover:border-[color:var(--gold)] hover:text-[color:var(--gold-hi)] transition">
            <LogOut size={13} /> Log Out
          </button>
        </div>

        {(saveMsg || error) && (
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-4">
            {saveMsg && <div className="rounded-lg bg-[color:var(--olive)]/25 border border-[color:var(--olive)]/40 px-4 py-2 text-sm" data-testid="admin-save-message">{saveMsg}</div>}
            {error && <div className="rounded-lg bg-red-900/30 border border-red-800/50 px-4 py-2 text-sm text-red-200" data-testid="admin-error-message">{error}</div>}
          </div>
        )}
      </header>

      {/* Tabs */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-8">
        <div className="flex flex-wrap gap-2 border-b border-warm pb-3">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`px-4 py-2 rounded-full text-xs tracking-[0.2em] uppercase transition ${
                tab === t.id
                  ? "bg-[color:var(--gold)] text-[color:var(--bg)]"
                  : "border border-warm text-cream/75 hover:border-[color:var(--gold)] hover:text-[color:var(--gold-hi)]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 space-y-8">
        {tab === "brand" && (
          <SectionCard title="Brand Identity" testid="admin-section-brand">
            <TextField label="Name" testid="brand-name" value={draft.brand?.name} onChange={(v) => updateField("brand", "name", v)} />
            <TextField label="Tagline (top-right of hero)" testid="brand-tagline-top" value={draft.brand?.tagline_top} onChange={(v) => updateField("brand", "tagline_top", v)} />
            <TextField label="Location (top-right)" testid="brand-location-top" value={draft.brand?.location_top} onChange={(v) => updateField("brand", "location_top", v)} />
            <ImageField label="Logo" testid="brand-logo" value={draft.brand?.logo_url} onChange={(v) => updateField("brand", "logo_url", v)} />
          </SectionCard>
        )}

        {tab === "hero" && (
          <SectionCard title="Hero Section" testid="admin-section-hero">
            <ImageField label="Background image" testid="hero-image" value={draft.hero?.image} onChange={(v) => updateField("hero", "image", v)} />
            <TextField label="Title line 1 (gold italic)" testid="hero-title-1" value={draft.hero?.title_line_1} onChange={(v) => updateField("hero", "title_line_1", v)} />
            <TextField label="Title line 2 (cream)" testid="hero-title-2" value={draft.hero?.title_line_2} onChange={(v) => updateField("hero", "title_line_2", v)} />
            <TextField label="Description" testid="hero-description" value={draft.hero?.description} onChange={(v) => updateField("hero", "description", v)} multiline rows={3} />
            <TextField label="Description emphasis (gold italic)" testid="hero-description-emphasis" value={draft.hero?.description_emphasis} onChange={(v) => updateField("hero", "description_emphasis", v)} />
            <TextField label="CTA button label" testid="hero-cta-label" value={draft.hero?.cta_label} onChange={(v) => updateField("hero", "cta_label", v)} />
            <TextField label="Secondary link label" testid="hero-secondary-link" value={draft.hero?.secondary_link_label} onChange={(v) => updateField("hero", "secondary_link_label", v)} />
            <TextField label="Scroll hint (footer of hero)" testid="hero-scroll-hint" value={draft.hero?.scroll_hint} onChange={(v) => updateField("hero", "scroll_hint", v)} />
            <TextField label="Tag left" testid="hero-tag-left" value={draft.hero?.tag_left} onChange={(v) => updateField("hero", "tag_left", v)} />
            <TextField label="Tag right" testid="hero-tag-right" value={draft.hero?.tag_right} onChange={(v) => updateField("hero", "tag_right", v)} />
          </SectionCard>
        )}

        {tab === "about" && (
          <>
            <SectionCard title="About Section — Text" testid="admin-section-about">
              <TextField label="Eyebrow" testid="about-eyebrow" value={draft.about?.eyebrow} onChange={(v) => updateField("about", "eyebrow", v)} />
              <TextField label="Title line 1" testid="about-title-1" value={draft.about?.title_line_1} onChange={(v) => updateField("about", "title_line_1", v)} />
              <TextField label="Title line 2 (gold italic)" testid="about-title-2" value={draft.about?.title_line_2} onChange={(v) => updateField("about", "title_line_2", v)} />
              <TextField label="Paragraph 1" testid="about-paragraph-1" value={draft.about?.paragraph_1} onChange={(v) => updateField("about", "paragraph_1", v)} multiline rows={4} />
              <TextField label="Paragraph 2" testid="about-paragraph-2" value={draft.about?.paragraph_2} onChange={(v) => updateField("about", "paragraph_2", v)} multiline rows={4} />
              <TextField label="Paragraph 3" testid="about-paragraph-3" value={draft.about?.paragraph_3} onChange={(v) => updateField("about", "paragraph_3", v)} multiline rows={2} />
              <ImageField label="Interior image" testid="about-image" value={draft.about?.interior_image} onChange={(v) => updateField("about", "interior_image", v)} />
              <TextField label="Quote line 1" testid="about-quote-1" value={draft.about?.quote_1} onChange={(v) => updateField("about", "quote_1", v)} />
              <TextField label="Quote line 2" testid="about-quote-2" value={draft.about?.quote_2} onChange={(v) => updateField("about", "quote_2", v)} />
              <TextField label="Quote attribution" testid="about-quote-attr" value={draft.about?.quote_attribution} onChange={(v) => updateField("about", "quote_attribution", v)} />
              <TextField label="Hours badge (multi-line)" testid="about-hours-badge" value={draft.about?.hours_badge} onChange={(v) => updateField("about", "hours_badge", v)} multiline rows={3} />
            </SectionCard>

            <SectionCard title="About — Stats" testid="admin-section-about-stats">
              {(draft.about?.stats || []).map((s, idx) => (
                <div key={idx} className="grid md:grid-cols-4 gap-3 items-end border-t border-warm pt-4">
                  <TextField label="Value (number)" testid={`about-stat-value-${idx}`} value={String(s.value ?? "")} onChange={(v) => updateArrayItem("about", "stats", idx, "value", Number(v) || 0)} />
                  <TextField label="Label" testid={`about-stat-label-${idx}`} value={s.label} onChange={(v) => updateArrayItem("about", "stats", idx, "label", v)} />
                  <TextField label="Suffix (e.g. +, k+)" testid={`about-stat-suffix-${idx}`} value={s.suffix} onChange={(v) => updateArrayItem("about", "stats", idx, "suffix", v)} />
                  <div className="flex gap-2">
                    <button onClick={() => moveArrayItem("about", "stats", idx, -1)} data-testid={`about-stat-up-${idx}`} className="p-2 rounded-full border border-warm hover:border-[color:var(--gold)]"><ArrowUp size={14} /></button>
                    <button onClick={() => moveArrayItem("about", "stats", idx, 1)} data-testid={`about-stat-down-${idx}`} className="p-2 rounded-full border border-warm hover:border-[color:var(--gold)]"><ArrowDown size={14} /></button>
                    <button onClick={() => removeArrayItem("about", "stats", idx)} data-testid={`about-stat-remove-${idx}`} className="p-2 rounded-full border border-red-800/50 text-red-300 hover:border-red-500"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
              <button onClick={() => addArrayItem("about", "stats", { value: 0, label: "New Stat", suffix: "" })} data-testid="about-stat-add" className="inline-flex items-center gap-2 rounded-full border border-[color:var(--gold)]/60 text-[color:var(--gold-hi)] px-4 py-2 text-xs tracking-wide uppercase hover:bg-[color:var(--gold)] hover:text-[color:var(--bg)] transition">
                <Plus size={13} /> Add Stat
              </button>
            </SectionCard>
          </>
        )}

        {tab === "grill" && (
          <>
            <SectionCard title="Grill Section — Text & Image" testid="admin-section-grill">
              <ImageField label="Background image" testid="grill-image" value={draft.grill?.image} onChange={(v) => updateField("grill", "image", v)} />
              <TextField label="Eyebrow" testid="grill-eyebrow" value={draft.grill?.eyebrow} onChange={(v) => updateField("grill", "eyebrow", v)} />
              <TextField label="Title line 1" testid="grill-title-1" value={draft.grill?.title_line_1} onChange={(v) => updateField("grill", "title_line_1", v)} />
              <TextField label="Title line 2 (italic gold)" testid="grill-title-2" value={draft.grill?.title_line_2} onChange={(v) => updateField("grill", "title_line_2", v)} />
            </SectionCard>

            <SectionCard title="Grill — Cards" testid="admin-section-grill-cards">
              {(draft.grill?.cards || []).map((c, idx) => (
                <div key={idx} className="border-t border-warm pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-xs tracking-[0.3em] uppercase text-muted-warm">Card {idx + 1}</div>
                    <div className="flex gap-2">
                      <button onClick={() => moveArrayItem("grill", "cards", idx, -1)} data-testid={`grill-card-up-${idx}`} className="p-2 rounded-full border border-warm hover:border-[color:var(--gold)]"><ArrowUp size={14} /></button>
                      <button onClick={() => moveArrayItem("grill", "cards", idx, 1)} data-testid={`grill-card-down-${idx}`} className="p-2 rounded-full border border-warm hover:border-[color:var(--gold)]"><ArrowDown size={14} /></button>
                      <button onClick={() => removeArrayItem("grill", "cards", idx)} data-testid={`grill-card-remove-${idx}`} className="p-2 rounded-full border border-red-800/50 text-red-300 hover:border-red-500"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <TextField label="Number (e.g. 01)" testid={`grill-card-n-${idx}`} value={c.n} onChange={(v) => updateArrayItem("grill", "cards", idx, "n", v)} />
                  <TextField label="Heading" testid={`grill-card-h-${idx}`} value={c.h} onChange={(v) => updateArrayItem("grill", "cards", idx, "h", v)} />
                  <TextField label="Text" testid={`grill-card-t-${idx}`} value={c.t} onChange={(v) => updateArrayItem("grill", "cards", idx, "t", v)} multiline rows={2} />
                </div>
              ))}
              <button onClick={() => addArrayItem("grill", "cards", { n: "04", h: "New card", t: "Description..." })} data-testid="grill-card-add" className="inline-flex items-center gap-2 rounded-full border border-[color:var(--gold)]/60 text-[color:var(--gold-hi)] px-4 py-2 text-xs tracking-wide uppercase hover:bg-[color:var(--gold)] hover:text-[color:var(--bg)] transition">
                <Plus size={13} /> Add Card
              </button>
            </SectionCard>
          </>
        )}

        {tab === "menu_flipbook" && (
          <>
            <SectionCard title="Menu Section — Text" testid="admin-section-menu">
              <TextField label="Eyebrow" testid="menu-eyebrow" value={draft.menu_flipbook?.eyebrow} onChange={(v) => updateField("menu_flipbook", "eyebrow", v)} />
              <TextField label="Title line 1" testid="menu-title-1" value={draft.menu_flipbook?.title_line_1} onChange={(v) => updateField("menu_flipbook", "title_line_1", v)} />
              <TextField label="Title line 2 (italic gold)" testid="menu-title-2" value={draft.menu_flipbook?.title_line_2} onChange={(v) => updateField("menu_flipbook", "title_line_2", v)} />
              <TextField label="Description" testid="menu-description" value={draft.menu_flipbook?.description} onChange={(v) => updateField("menu_flipbook", "description", v)} multiline rows={3} />
            </SectionCard>

            <SectionCard title="Menu Pages (Flipbook Images)" testid="admin-section-menu-pages">
              <p className="text-sm text-cream/70">
                Upload menu page images in the order you want them displayed. You can add, remove, and reorder pages.
              </p>
              {(draft.menu_flipbook?.pages || []).map((p, idx) => (
                <div key={idx} className="border-t border-warm pt-4 flex items-start gap-3">
                  <div className="text-xs tracking-[0.3em] uppercase text-muted-warm w-16 pt-3">Page {idx + 1}</div>
                  <div className="flex-1">
                    <ImageField label={`Page ${idx + 1} image`} testid={`menu-page-${idx}`} value={p} onChange={(v) => updateSimpleArrayItem("menu_flipbook", "pages", idx, v)} />
                  </div>
                  <div className="flex flex-col gap-2 pt-6">
                    <button onClick={() => moveArrayItem("menu_flipbook", "pages", idx, -1)} data-testid={`menu-page-up-${idx}`} className="p-2 rounded-full border border-warm hover:border-[color:var(--gold)]"><ArrowUp size={14} /></button>
                    <button onClick={() => moveArrayItem("menu_flipbook", "pages", idx, 1)} data-testid={`menu-page-down-${idx}`} className="p-2 rounded-full border border-warm hover:border-[color:var(--gold)]"><ArrowDown size={14} /></button>
                    <button onClick={() => removeArrayItem("menu_flipbook", "pages", idx)} data-testid={`menu-page-remove-${idx}`} className="p-2 rounded-full border border-red-800/50 text-red-300 hover:border-red-500"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
              <button onClick={() => addSimpleArrayItem("menu_flipbook", "pages", "")} data-testid="menu-page-add" className="inline-flex items-center gap-2 rounded-full border border-[color:var(--gold)]/60 text-[color:var(--gold-hi)] px-4 py-2 text-xs tracking-wide uppercase hover:bg-[color:var(--gold)] hover:text-[color:var(--bg)] transition">
                <Plus size={13} /> Add Page
              </button>
            </SectionCard>
          </>
        )}

        {tab === "testimonials" && (
          <>
            <SectionCard title="Testimonials — Headings" testid="admin-section-testimonials">
              <TextField label="Eyebrow" testid="testimonials-eyebrow" value={draft.testimonials?.eyebrow} onChange={(v) => updateField("testimonials", "eyebrow", v)} />
              <TextField label="Title line 1" testid="testimonials-title-1" value={draft.testimonials?.title_line_1} onChange={(v) => updateField("testimonials", "title_line_1", v)} />
              <TextField label="Title line 2 (italic gold)" testid="testimonials-title-2" value={draft.testimonials?.title_line_2} onChange={(v) => updateField("testimonials", "title_line_2", v)} />
            </SectionCard>
            <SectionCard title="Testimonials — Quotes" testid="admin-section-testimonials-quotes">
              {(draft.testimonials?.quotes || []).map((q, idx) => (
                <div key={idx} className="border-t border-warm pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-xs tracking-[0.3em] uppercase text-muted-warm">Quote {idx + 1}</div>
                    <div className="flex gap-2">
                      <button onClick={() => moveArrayItem("testimonials", "quotes", idx, -1)} data-testid={`testimonial-up-${idx}`} className="p-2 rounded-full border border-warm hover:border-[color:var(--gold)]"><ArrowUp size={14} /></button>
                      <button onClick={() => moveArrayItem("testimonials", "quotes", idx, 1)} data-testid={`testimonial-down-${idx}`} className="p-2 rounded-full border border-warm hover:border-[color:var(--gold)]"><ArrowDown size={14} /></button>
                      <button onClick={() => removeArrayItem("testimonials", "quotes", idx)} data-testid={`testimonial-remove-${idx}`} className="p-2 rounded-full border border-red-800/50 text-red-300 hover:border-red-500"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <TextField label="Quote" testid={`testimonial-q-${idx}`} value={q.q} onChange={(v) => updateArrayItem("testimonials", "quotes", idx, "q", v)} multiline rows={3} />
                  <TextField label="Name" testid={`testimonial-n-${idx}`} value={q.n} onChange={(v) => updateArrayItem("testimonials", "quotes", idx, "n", v)} />
                  <TextField label="Role/Location" testid={`testimonial-r-${idx}`} value={q.r} onChange={(v) => updateArrayItem("testimonials", "quotes", idx, "r", v)} />
                </div>
              ))}
              <button onClick={() => addArrayItem("testimonials", "quotes", { q: "", n: "", r: "" })} data-testid="testimonial-add" className="inline-flex items-center gap-2 rounded-full border border-[color:var(--gold)]/60 text-[color:var(--gold-hi)] px-4 py-2 text-xs tracking-wide uppercase hover:bg-[color:var(--gold)] hover:text-[color:var(--bg)] transition">
                <Plus size={13} /> Add Quote
              </button>
            </SectionCard>
          </>
        )}

        {tab === "contact" && (
          <SectionCard title="Contact Section" testid="admin-section-contact">
            <TextField label="Eyebrow" testid="contact-eyebrow" value={draft.contact?.eyebrow} onChange={(v) => updateField("contact", "eyebrow", v)} />
            <TextField label="Title line 1" testid="contact-title-1" value={draft.contact?.title_line_1} onChange={(v) => updateField("contact", "title_line_1", v)} />
            <TextField label="Title line 2 (italic gold)" testid="contact-title-2" value={draft.contact?.title_line_2} onChange={(v) => updateField("contact", "title_line_2", v)} />
            <TextField label="Address" testid="contact-address" value={draft.contact?.address} onChange={(v) => updateField("contact", "address", v)} />
            <TextField label="Phone (display)" testid="contact-phone" value={draft.contact?.phone} onChange={(v) => updateField("contact", "phone", v)} />
            <TextField label="Phone link (tel:...)" testid="contact-phone-link" value={draft.contact?.phone_link} onChange={(v) => updateField("contact", "phone_link", v)} />
            <TextField label="Email" testid="contact-email" value={draft.contact?.email} onChange={(v) => updateField("contact", "email", v)} />
            <TextField label="Hours" testid="contact-hours" value={draft.contact?.hours} onChange={(v) => updateField("contact", "hours", v)} />
            <TextField label="Instagram URL" testid="contact-instagram" value={draft.contact?.instagram} onChange={(v) => updateField("contact", "instagram", v)} />
            <TextField label="Facebook URL" testid="contact-facebook" value={draft.contact?.facebook} onChange={(v) => updateField("contact", "facebook", v)} />
            <TextField label="TikTok URL" testid="contact-tiktok" value={draft.contact?.tiktok} onChange={(v) => updateField("contact", "tiktok", v)} />
            <TextField label="Uber Eats URL" testid="contact-uber-eats" value={draft.contact?.uber_eats} onChange={(v) => updateField("contact", "uber_eats", v)} />
            <TextField label="Glovo URL" testid="contact-glovo" value={draft.contact?.glovo} onChange={(v) => updateField("contact", "glovo", v)} />
            <TextField label="Bolt Food URL" testid="contact-bolt-food" value={draft.contact?.bolt_food} onChange={(v) => updateField("contact", "bolt_food", v)} />
            <TextField label="Reserve button label" testid="contact-reserve-label" value={draft.contact?.reserve_label} onChange={(v) => updateField("contact", "reserve_label", v)} />
            <TextField label="Google Maps embed URL" testid="contact-map-src" value={draft.contact?.map_src} onChange={(v) => updateField("contact", "map_src", v)} multiline rows={2} />
          </SectionCard>
        )}

        {tab === "footer" && (
          <>
            <SectionCard title="Footer — Text" testid="admin-section-footer">
              <TextField label="Tagline" testid="footer-tagline" value={draft.footer?.tagline} onChange={(v) => updateField("footer", "tagline", v)} multiline rows={2} />
              <TextField label="Copyright name" testid="footer-copyright" value={draft.footer?.copyright} onChange={(v) => updateField("footer", "copyright", v)} />
              <TextField label="Location line" testid="footer-location" value={draft.footer?.location_line} onChange={(v) => updateField("footer", "location_line", v)} />
            </SectionCard>

            <SectionCard title="Footer — Marquee lines" testid="admin-section-footer-marquee">
              {(draft.footer?.marquee_lines || []).map((line, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <input
                    type="text"
                    value={line}
                    onChange={(e) => updateSimpleArrayItem("footer", "marquee_lines", idx, e.target.value)}
                    data-testid={`footer-marquee-${idx}`}
                    className="flex-1 bg-bg border border-warm rounded-lg px-4 py-3 text-cream outline-none focus:border-[color:var(--gold)]"
                  />
                  <button onClick={() => moveArrayItem("footer", "marquee_lines", idx, -1)} data-testid={`footer-marquee-up-${idx}`} className="p-2 rounded-full border border-warm hover:border-[color:var(--gold)]"><ArrowUp size={14} /></button>
                  <button onClick={() => moveArrayItem("footer", "marquee_lines", idx, 1)} data-testid={`footer-marquee-down-${idx}`} className="p-2 rounded-full border border-warm hover:border-[color:var(--gold)]"><ArrowDown size={14} /></button>
                  <button onClick={() => removeArrayItem("footer", "marquee_lines", idx)} data-testid={`footer-marquee-remove-${idx}`} className="p-2 rounded-full border border-red-800/50 text-red-300 hover:border-red-500"><Trash2 size={14} /></button>
                </div>
              ))}
              <button onClick={() => addSimpleArrayItem("footer", "marquee_lines", "New line")} data-testid="footer-marquee-add" className="inline-flex items-center gap-2 rounded-full border border-[color:var(--gold)]/60 text-[color:var(--gold-hi)] px-4 py-2 text-xs tracking-wide uppercase hover:bg-[color:var(--gold)] hover:text-[color:var(--bg)] transition">
                <Plus size={13} /> Add Line
              </button>
            </SectionCard>
          </>
        )}
      </main>
    </div>
  );
}
